<template>
  <form :action="formAction" :method="httpMethod" class="storage-form-page" @submit="submitting = true">
    <input v-if="csrfToken && httpMethod !== 'get'" type="hidden" name="authenticity_token" :value="csrfToken">
    <input v-if="effectiveMethod !== 'post' && effectiveMethod !== 'get'" type="hidden" name="_method" :value="effectiveMethod">

    <div v-if="errors.length" class="alert alert-danger">
      <strong>Unable to save storage.</strong>
      <ul class="mb-0">
        <li v-for="message in errors" :key="message">{{ message }}</li>
      </ul>
    </div>

    <table class="hv-form-table storage-form-table">
      <tr>
        <th>Storage Name</th>
        <td>
          <input v-model="name" name="storage[name]" type="text" class="form-control" required :readonly="lockPoolXmlFields">
        </td>
      </tr>

      <tr>
        <th>Storage Type</th>
        <td>
          <select v-model="storageType" name="storage[storage_type]" class="form-control" :disabled="lockPoolXmlFields">
            <option v-for="option in storageTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
          <input v-if="lockPoolXmlFields" type="hidden" name="storage[storage_type]" :value="storageType">
        </td>
      </tr>

      <tr>
        <th>Description</th>
        <td>
          <input v-model="description" name="storage[description]" type="text" class="form-control">
        </td>
      </tr>

      <tr>
        <th>HyperVisors</th>
        <td>
          <select v-model="hypervisorScope" name="storage[hypervisor_scope]" class="form-control">
            <option v-for="option in hypervisorScopeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>

          <div v-if="hypervisorScope === 'selected'" class="storage-form-page__multi-select">
            <label
              v-for="option in hypervisorOptions"
              :key="option.value"
              class="storage-form-page__checkbox"
            >
              <input
                v-model="selectedHypervisorIds"
                type="checkbox"
                name="storage[hyper_visor_ids][]"
                :value="String(option.value)"
              >
              <span>{{ option.label }}</span>
            </label>
          </div>
        </td>
      </tr>

      <template v-if="storageType === 'ceph'">
        <tr>
          <th>Monitor Hosts</th>
          <td>
            <input v-model="ceph.mon_hosts" name="ceph_storage[mon_hosts]" type="text" class="form-control" placeholder="10.0.0.10,10.0.0.11,10.0.0.12">
          </td>
        </tr>
        <tr>
          <th>Pool</th>
          <td>
            <input v-model="ceph.pool" name="ceph_storage[pool]" type="text" class="form-control" placeholder="rbd">
          </td>
        </tr>
        <tr>
          <th>User</th>
          <td>
            <input v-model="ceph.user" name="ceph_storage[user]" type="text" class="form-control" placeholder="client.libvirt">
          </td>
        </tr>
        <tr>
          <th>Secret UUID</th>
          <td>
            <input v-model="ceph.secret_uuid" name="ceph_storage[secret_uuid]" type="text" class="form-control">
          </td>
        </tr>
        <tr>
          <th>Image Format</th>
          <td>
            <input v-model="ceph.image_format" name="ceph_storage[image_format]" type="text" class="form-control" placeholder="raw">
          </td>
        </tr>
      </template>

      <template v-else>
        <tr v-if="lockNfsIdentityFields">
          <th>Note</th>
          <td class="text-muted">
            Storage Name / Storage Type / NFS Server / Export Path / Target Path / NFS Version are locked after storage creation.
          </td>
        </tr>
        <tr>
          <th>NFS Server</th>
          <td>
            <input v-model="nfs.host" name="nfs_storage[host]" type="text" class="form-control" placeholder="nfs.example.com" :disabled="lockNfsIdentityFields">
          </td>
        </tr>
        <tr>
          <th>Export Path</th>
          <td>
            <input v-model="nfs.export_path" name="nfs_storage[export_path]" type="text" class="form-control" placeholder="/exports/scc-images" :disabled="lockNfsIdentityFields">
          </td>
        </tr>
        <tr>
          <th>Target Path</th>
          <td>
            <input v-model="nfs.target_path" name="nfs_storage[target_path]" type="text" class="form-control" placeholder="/var/scc/images" :disabled="lockNfsIdentityFields">
          </td>
        </tr>
        <tr>
          <th>User Name</th>
          <td>
            <input v-model="nfs.username" name="nfs_storage[username]" type="text" class="form-control" placeholder="Optional">
          </td>
        </tr>
        <tr>
          <th>Password</th>
          <td>
            <input v-model="nfs.password" name="nfs_storage[password]" type="password" class="form-control" placeholder="Optional">
          </td>
        </tr>
        <tr>
          <th>NFS Version</th>
          <td>
            <input v-model="nfs.version" name="nfs_storage[version]" type="text" class="form-control" placeholder="4.1" :disabled="lockNfsIdentityFields">
          </td>
        </tr>
      </template>

      <tr>
        <th>Enabled</th>
        <td>
          <input type="hidden" name="storage[enabled]" value="0">
          <label class="storage-form-page__checkbox">
            <input v-model="enabled" name="storage[enabled]" type="checkbox" value="1">
            <span>Enabled</span>
          </label>
        </td>
      </tr>
    </table>

    <div class="vm-form-actions">
      <button type="submit" class="btn btn-primary" :disabled="submitting">{{ submitLabel }}</button>
      <a :href="backUrl" class="btn btn-default">Back</a>
    </div>
  </form>
</template>

<script setup>
import { ref } from "vue"

const props = defineProps({
  formAction: { type: String, required: true },
  formMethod: { type: String, default: "post" },
  submitLabel: { type: String, default: "Save" },
  backUrl: { type: String, required: true },
  storageTypes: {
    type: Array,
    default: () => [
      { label: "NFS", value: "nfs" },
      { label: "CEPH", value: "ceph" }
    ]
  },
  initialName: { type: String, default: "" },
  initialDescription: { type: String, default: "" },
  initialEnabled: { type: Boolean, default: true },
  initialStorageType: { type: String, default: "nfs" },
  lockPoolXmlFields: { type: Boolean, default: false },
  lockNfsIdentityFields: { type: Boolean, default: false },
  initialHypervisorScope: { type: String, default: "all" },
  initialHypervisorIds: { type: Array, default: () => [] },
  hypervisorScopeOptions: {
    type: Array,
    default: () => [
      { label: "All", value: "all" },
      { label: "Selected", value: "selected" }
    ]
  },
  hypervisorOptions: { type: Array, default: () => [] },
  initialNfs: { type: Object, default: () => ({}) },
  initialCeph: { type: Object, default: () => ({}) },
  errors: { type: Array, default: () => [] }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const effectiveMethod = props.formMethod.toLowerCase()
const httpMethod = effectiveMethod === "get" ? "get" : "post"

const name = ref(props.initialName)
const description = ref(props.initialDescription)
const enabled = ref(!!props.initialEnabled)
const storageType = ref(props.initialStorageType || "nfs")
const hypervisorScope = ref(props.initialHypervisorScope || "all")
const selectedHypervisorIds = ref((props.initialHypervisorIds || []).map((value) => String(value)))
const nfs = ref({
  host: props.initialNfs?.host || "",
  export_path: props.initialNfs?.export_path || "",
  target_path: props.initialNfs?.target_path || "",
  username: props.initialNfs?.username || "",
  password: props.initialNfs?.password || "",
  version: props.initialNfs?.version || ""
})
const ceph = ref({
  mon_hosts: props.initialCeph?.mon_hosts || "",
  pool: props.initialCeph?.pool || "",
  user: props.initialCeph?.user || "",
  secret_uuid: props.initialCeph?.secret_uuid || "",
  image_format: props.initialCeph?.image_format || ""
})
const submitting = ref(false)
</script>
